import React, { type ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Microscope, FlaskConical,
    Clock, Eye, CheckCircle, UploadCloud, AlertCircle,
    BookOpen,
} from "lucide-react";
import PathologyServices from "../../shared/ApiServices/PathologyServices";

const PATHOLOGY_NAV = [
    { path: "/pathology", label: "Overview", Icon: LayoutDashboard, exact: true },
    { path: "/pathology/slides", label: "Slide Library", Icon: Microscope, exact: false },
    { path: "/pathology/user-guide", label: "User Guide", Icon: BookOpen, exact: false },
] as const;

interface PathologyStats {
    processing: number;
    underReview: number;
    failed: number;
    totalStudies: number;
    totalUploads: number;
}

let _pathoCache: PathologyStats | null = null;
let _pathoCacheAt = 0;
const CACHE_TTL = 5 * 60_000;

interface PathologyShellProps { children: ReactNode; pageTitle?: string }

export const PathologyShell: React.FC<PathologyShellProps> = ({ children }) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const activeNav = PATHOLOGY_NAV.find(n =>
        n.exact ? pathname === n.path : pathname === n.path || pathname.startsWith(n.path + "/")
    ) ?? PATHOLOGY_NAV[0];

    const [pathoStats, setPathoStats] = useState<PathologyStats | null>(_pathoCache);
    const [statsLoading, setStatsLoading] = useState(!_pathoCache);

    useEffect(() => {
        let cancelled = false;
        const now = Date.now();

        if (_pathoCache && now - _pathoCacheAt < CACHE_TTL) { setStatsLoading(false); return; }

        setStatsLoading(true);

        PathologyServices.getPathologyStats()
            .then((res: any) => {
                if (cancelled) return;
                const d = res?.dashboard ?? res?.data?.dashboard ?? {};
                const u = res?.uploads ?? res?.data?.uploads ?? {};
                const s = res?.studies ?? res?.data?.studies ?? {};
                _pathoCache = {
                    processing: d.studiesProcessing ?? 0,
                    underReview: d.studiesUnderReview ?? 0,
                    failed: d.studiesFailed ?? 0,
                    totalStudies: s.totalStudies ?? 0,
                    totalUploads: u.total ?? 0,
                };
                _pathoCacheAt = Date.now();
                if (!cancelled) setPathoStats(_pathoCache);
            })
            .catch(() => { })
            .finally(() => { if (!cancelled) setStatsLoading(false); });

        return () => { cancelled = true; };
    }, []);

    const pathoItems = pathoStats ? [
        { value: pathoStats.totalStudies, label: "Studies", color: "#16A34A", Icon: CheckCircle, show: true },
        { value: pathoStats.processing, label: "Processing", color: "#D97706", Icon: Clock, show: true },
        { value: pathoStats.underReview, label: "Reviewing", color: "#7C3AED", Icon: Eye, show: true },
        { value: pathoStats.totalUploads, label: "Uploads", color: "var(--v2-primary-500)", Icon: UploadCloud, show: true },
        { value: pathoStats.failed, label: "Failed", color: "#DC2626", Icon: AlertCircle, show: pathoStats.failed > 0 },
    ] : [];

    const skeletonWidths = [40, 56, 52, 48];

    return (
        <div className="min-h-full flex flex-col bg-[var(--v2-bg-main)] text-[var(--v2-text-primary)]">
            <section
                aria-label="Pathology navigation"
                className="relative flex-shrink-0 mb-4 overflow-hidden rounded-2xl border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)]"
                style={{ boxShadow: 'var(--v2-shadow-card)' }}
            >
                <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'var(--v2-bg-hero)' }} />

                <div
                    aria-hidden
                    className="absolute inset-x-0 top-0 h-[3px]"
                    style={{ background: 'linear-gradient(90deg, var(--v2-primary-500) 0%, var(--v2-primary-400) 55%, transparent 100%)' }}
                />

                <div className="relative z-10 flex items-center gap-3 px-5 h-[52px]">

                    <div className="flex items-center gap-2.5 flex-shrink-0">
                        <span
                            className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
                            style={{ background: 'var(--v2-primary-50, rgba(4,94,184,0.08))', color: 'var(--v2-primary-500)' }}
                        >
                            <FlaskConical className="w-4 h-4" strokeWidth={2} />
                        </span>
                        <div className="flex flex-col gap-0.5">
                            <span className="text-[13.5px] font-black text-[var(--v2-text-heading)] tracking-tight leading-tight whitespace-nowrap">
                                Pathology
                            </span>
                            <span className="text-[9px] font-semibold tracking-wider uppercase text-[var(--v2-text-muted)] leading-none">
                                Digital Pathology
                            </span>
                        </div>
                    </div>

                    <div className="w-px h-5 bg-[var(--v2-border-subtle)] flex-shrink-0" />

                    <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto overflow-y-hidden v2-scrollbar">
                        {statsLoading ? (
                            <div className="flex items-center gap-1.5">
                                {skeletonWidths.map((w, i) => (
                                    <div key={i} className="h-6 rounded-lg bg-[var(--v2-border-subtle)]" style={{ width: w + 28 }} />
                                ))}
                            </div>
                        ) : (
                            pathoItems.filter(s => s.show).map((item) => {
                                const StatIcon = item.Icon;
                                return (
                                    <div
                                        key={item.label}
                                        className="flex items-center gap-1.5 pl-1.5 pr-2.5 h-7 rounded-lg flex-shrink-0"
                                        style={{ background: `color-mix(in srgb, ${item.color} 12%, transparent)` }}
                                    >
                                        <span
                                            className="flex items-center justify-center w-4 h-4 rounded-md flex-shrink-0"
                                            style={{ color: item.color }}
                                        >
                                            <StatIcon className="w-full h-full" strokeWidth={2.3} />
                                        </span>
                                        <span className="text-[13px] font-black tabular-nums leading-none" style={{ color: item.color }}>
                                            {item.value}
                                        </span>
                                        <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--v2-text-muted)] whitespace-nowrap leading-none">
                                            {item.label}
                                        </span>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <nav
                        aria-label="Pathology navigation"
                        className="flex items-center gap-1 flex-shrink-0"
                    >
                        <div
                            className="flex items-center gap-0.5 p-0.5 rounded-xl"
                            style={{ background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-subtle)' }}
                        >
                            {PATHOLOGY_NAV.map(nav => {
                                const isActive = nav === activeNav;
                                const NavIcon = nav.Icon;
                                return (
                                    <button
                                        key={nav.path}
                                        type="button"
                                        onClick={() => navigate(nav.path)}
                                        aria-current={isActive ? "page" : undefined}
                                        title={nav.label}
                                        className={[
                                            "inline-flex items-center gap-1.5 px-3 h-7 rounded-[10px]",
                                            "text-[12px] font-semibold cursor-pointer border-0",
                                            "transition-all duration-150 whitespace-nowrap",
                                            "focus-visible:ring-2 focus-visible:ring-[var(--v2-primary-400)]",
                                        ].join(" ")}
                                        style={isActive
                                            ? { background: 'var(--v2-primary-500)', color: '#fff', boxShadow: '0 1px 4px rgba(4,94,184,0.35)' }
                                            : { background: 'transparent', color: 'var(--v2-text-muted)' }
                                        }
                                    >
                                        <NavIcon className="w-[13px] h-[13px] flex-shrink-0" strokeWidth={isActive ? 2.2 : 1.8} />
                                        {nav.label}
                                    </button>
                                );
                            })}
                        </div>
                    </nav>

                </div>
            </section>

            <div className="flex-1 overflow-y-auto v2-scrollbar">
                <div className="v2-animate-fade-in-up">
                    {children}
                </div>
            </div>
        </div>
    );
};
